import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Platform } from 'react-native';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const getCurrentUtcSeconds = (): number => {
  const currentDate = new Date();
  const utcSeconds = Math.floor(currentDate.getTime() / 1000);
  return utcSeconds;
};

export const getPastUtcSeconds = (rollBackSeconds: number): number => {
  //rollback the current time by a number of seconds
  const currentDate: Date = new Date();
  const rollbackDate: Date = new Date(
    currentDate.getTime() - rollBackSeconds * 1000
  );

  const utcSeconds: number = Math.floor(rollbackDate.getTime() / 1000);

  return utcSeconds;
};

export const getDateInUtcSeconds = (d: Date): number => {
  const utcSeconds = Math.floor(d.getTime() / 1000);
  return utcSeconds;
};

export const getDateInLocalSeconds = (d: Date, tzOffset: number): number => {
  const tzOffsetSeconds = (tzOffset ?? 0) * 60; //offset time comes in minutes, convert to seconds
  const localSeconds = Math.floor(d.getTime() / 1000) - tzOffsetSeconds;
  return localSeconds;
};

export const getDateFromUtcSeconds = (utcSeconds: number): Date => {
  const milliseconds = utcSeconds * 1000;
  return new Date(milliseconds);
};

export const getDateFromLocalSeconds = (
  utcSeconds: number,
  tzOffset: number
): Date => {
  const milliseconds = utcSeconds * 1000;
  const tzOffsetmilliseconds = (tzOffset ?? 0) * 60 * 1000;
  return new Date(milliseconds + tzOffsetmilliseconds);
};

// FUNCTION: getDateString
// PURPOSE: Return date in MM/DD/YYYY format
export const getDateString = (d: Date): string => {
  const pad = (n: number) => (n < 10 ? '0' + n : n);
  const month = pad(d.getMonth() + 1); // getMonth() is zero-based
  const day = pad(d.getDate());
  const year = d.getFullYear();
  const formattedDate = `${month}/${day}/${year}`;
  return formattedDate;
};

//FUNCTION: getTimeString
// PURPOSE: return time in HH:MM:SS format
export const getTimeString = (d: Date): string => {
  const pad = (n: number) => (n < 10 ? '0' + n : n);

  let hours = d.getHours();
  const minutes = pad(d.getMinutes());
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  const formattedTime = `${pad(hours)}:${minutes}${ampm}`;
  return formattedTime;
};

export const getCurrentDate = () => {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!
  const yyyy = today.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
};

export const getOneMonthBackDate = () => {
  const today = new Date();
  const oneMonthBack = new Date(today.setMonth(today.getMonth() - 1));
  const dd = String(oneMonthBack.getDate()).padStart(2, '0');
  const mm = String(oneMonthBack.getMonth() + 1).padStart(2, '0'); // January is 0!
  const yyyy = oneMonthBack.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
};

// Format the date to mm/dd/yyyy
// Input format is 2024-05-20T21:46:52.000Z
export const formattedDate = (inputDateTimeStr: string | null) => {
  if (inputDateTimeStr !== null) {
    const inputDate = new Date(inputDateTimeStr);
    return inputDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
};

// Format the time to HH:MM
// Input format is 2024-05-20T21:46:52.000Z
export const formattedTimeNoSeconds = (
  inputDateTimeStr: string | null,
  tz: string,
  tzOffset?: number | null
) => {
  if (!inputDateTimeStr) return null; // Handle null input

  try {
    // For mobile, we need to handle timezone conversion manually
    // since the dayjs timezone plugin doesn't work reliably on React Native
    const inputDateTime = dayjs.utc(inputDateTimeStr);

    let adjustedTime;

    if (Platform.OS === 'web') {
      // On web, use the timezone plugin
      adjustedTime = inputDateTime.tz(tz);
    } else {
      // On mobile, use the tz_offset value (in minutes) to calculate the offset
      // tz_offset represents the offset from UTC in minutes
      // For example: Denver is UTC-6, so tz_offset should be -360 minutes
      // But if it's stored as positive, we need to negate it
      const offsetMinutes = -(tzOffset || 0);
      const offsetHours = offsetMinutes / 60;
      adjustedTime = inputDateTime.add(offsetHours, 'hour');
    }

    // Format time with timezone information
    const formattedTime = adjustedTime.format('hh:mm A') + ` ${tz}`;

    return formattedTime;
  } catch (error) {
    console.error('Error formatting time:', error);
    // Fallback: return a simple formatted time without timezone conversion
    try {
      const fallbackTime = dayjs(inputDateTimeStr).format('HH:mm');
      return fallbackTime + ` ${tz}`;
    } catch (fallbackError) {
      return inputDateTimeStr;
    }
  }
};

export const secondsToHHMMSS = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  // Pad the values with leading zeros if necessary
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(secs).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}.${formattedSeconds}`;
};
